import { useState, useMemo } from 'react';
import { useSupabaseAppointments } from '../../../hooks/useSupabaseAppointments';
import type { Appointment } from '../../../mocks/appointments';
import { statusLabels } from '../../../mocks/appointments';

const monthNames = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function formatDateNice(iso: string) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} de ${monthNames[d.getMonth()]} de ${d.getFullYear()}`;
}

function isUpcoming(iso: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso + 'T00:00:00');
  return d >= today;
}

export default function MiReservaPage() {
  const { appointments, types, loading } = useSupabaseAppointments();
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);

  const userAppointments = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const isEmail = q.includes('@');
    // Para teléfono: comparar solo dígitos, últimos 9 (tolera prefijo +34, espacios, guiones)
    const qPhone = q.replace(/\D/g, '').slice(-9);
    return appointments
      .filter((a) => {
        if (isEmail) {
          return a.email?.toLowerCase() === q.toLowerCase();
        }
        if (!a.phone || qPhone.length < 6) return false;
        return a.phone.replace(/\D/g, '').slice(-9) === qPhone;
      })
      .sort((a, b) => {
        const da = new Date(a.preferred_date + 'T' + a.preferred_time).getTime();
        const db = new Date(b.preferred_date + 'T' + b.preferred_time).getTime();
        return db - da;
      });
  }, [appointments, query]);

  const upcoming = useMemo(
    () => userAppointments.filter((a) => a.status !== 'cancelled' && isUpcoming(a.preferred_date)),
    [userAppointments]
  );

  const past = useMemo(
    () => userAppointments.filter((a) => a.status === 'cancelled' || !isUpcoming(a.preferred_date)),
    [userAppointments]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearched(true);
  };

  return (
    <div className="w-full min-h-[80vh] bg-white py-20 md:py-32">
      <div className="px-6 lg:px-[7%]">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">
              Clientes
            </span>
            <h1 className="font-serif text-brand-black text-3xl md:text-4xl font-semibold leading-[1.15] mt-4">
              Consultar mi <span className="italic">Reserva</span>
            </h1>
            <p className="font-sans text-brand-gray-500 text-[15px] leading-relaxed mt-4 max-w-lg mx-auto">
              Introduce el email o el teléfono con el que hiciste tu reserva para ver el estado de tus citas, confirmaciones y pagos.
            </p>
          </div>

          {/* Search form */}
          <form
            onSubmit={handleSearch}
            className="max-w-md mx-auto mb-12 md:mb-16"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                required
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="tu@email.com o teléfono"
                className="flex-1 bg-white border border-brand-gray-200 rounded-xl px-5 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-brand-black text-white font-sans text-sm tracking-widest uppercase px-8 py-3.5 rounded-full hover:bg-brand-gray-600 transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Buscando...
                  </span>
                ) : (
                  'Buscar'
                )}
              </button>
            </div>
          </form>

          {/* Loading state */}
          {loading && (
            <div className="text-center py-10">
              <i className="ri-loader-4-line animate-spin text-brand-gray-400 text-2xl" />
              <p className="font-sans text-brand-gray-400 text-sm mt-3">
                Cargando citas...
              </p>
            </div>
          )}

          {/* Results */}
          {!loading && searched && (
            <>
              {userAppointments.length === 0 ? (
                <div className="text-center py-16 bg-brand-gray-50 rounded-[24px]">
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-5">
                    <i className="ri-calendar-line text-brand-gray-400 text-2xl" />
                  </div>
                  <h3 className="font-serif text-brand-black text-xl font-medium">
                    No encontramos reservas
                  </h3>
                  <p className="font-sans text-brand-gray-500 text-[15px] leading-relaxed mt-3 max-w-sm mx-auto">
                    No hay citas asociadas a <strong className="text-brand-black">{query}</strong>. Verifica que el email o teléfono sea el mismo con el que realizaste la reserva.
                  </p>
                  <div className="mt-6 space-y-2">
                    <p className="font-sans text-brand-gray-400 text-sm">
                      ¿Necesitas ayuda? Reserva una cita en:
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <a
                        href="/alta-costura"
                        className="font-sans text-brand-black text-sm border-b border-brand-black pb-0.5 hover:text-brand-gray-600 hover:border-brand-gray-600 transition-colors whitespace-nowrap"
                      >
                        Alta Costura
                      </a>
                      <a
                        href="/pret-a-porter"
                        className="font-sans text-brand-black text-sm border-b border-brand-black pb-0.5 hover:text-brand-gray-600 hover:border-brand-gray-600 transition-colors whitespace-nowrap"
                      >
                        Arreglos
                      </a>
                      <a
                        href="/vestidos-novia"
                        className="font-sans text-brand-black text-sm border-b border-brand-black pb-0.5 hover:text-brand-gray-600 hover:border-brand-gray-600 transition-colors whitespace-nowrap"
                      >
                        Vestidos de Novia
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  {/* Upcoming appointments */}
                  {upcoming.length > 0 && (
                    <div>
                      <h2 className="font-serif text-brand-black text-xl font-medium mb-5 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-brand-sage" />
                        Próximas citas
                        <span className="font-sans text-brand-gray-400 text-sm font-normal">
                          ({upcoming.length})
                        </span>
                      </h2>
                      <div className="space-y-4">
                        {upcoming.map((appt) => (
                          <AppointmentCard
                            key={appt.id}
                            appt={appt}
                            type={types.find((t) => t.name === appt.service_type)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past / cancelled */}
                  {past.length > 0 && (
                    <div>
                      <h2 className="font-serif text-brand-black text-xl font-medium mb-5 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-brand-gray-300" />
                        Citas pasadas
                        <span className="font-sans text-brand-gray-400 text-sm font-normal">
                          ({past.length})
                        </span>
                      </h2>
                      <div className="space-y-4">
                        {past.map((appt) => (
                          <AppointmentCard
                            key={appt.id}
                            appt={appt}
                            type={types.find((t) => t.name === appt.service_type)}
                            isPast
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Info tip */}
          {!searched && !loading && (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-brand-gray-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-brand-gray-400 text-xl" />
              </div>
              <p className="font-sans text-brand-gray-400 text-sm">
                Introduce tu email o teléfono para consultar el estado de tus reservas.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppointmentCard({
  appt,
  type,
  isPast,
}: {
  appt: Appointment;
  type?: import('../../../mocks/appointments').AppointmentType;
  isPast?: boolean;
}) {
  const st = statusLabels[appt.status];
  const hasPrice = type?.price !== undefined && type.price > 0;

  return (
    <div
      className={`bg-white border rounded-2xl p-5 md:p-6 transition-all ${
        isPast ? 'border-brand-gray-200 opacity-80' : 'border-brand-gray-200 hover:border-brand-gray-300'
      }`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
        {/* Date block */}
        <div className="flex-shrink-0 w-full md:w-28 text-center md:text-left">
          <p className="font-serif text-brand-black text-2xl font-medium leading-none">
            {new Date(appt.preferred_date + 'T00:00:00').getDate()}
          </p>
          <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider mt-1">
            {monthNames[new Date(appt.preferred_date + 'T00:00:00').getMonth()]}
          </p>
          <p className="font-sans text-brand-black text-sm font-medium mt-1">
            {appt.preferred_time}
          </p>
        </div>

        {/* Details */}
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${st?.bg} ${st?.color}`}
            >
              {st?.icon && <i className={st.icon} />}
              {st?.label}
            </span>

            {hasPrice && (
              <>
                {appt.paid ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-emerald-50 text-emerald-700">
                    <i className="ri-checkbox-circle-line" />
                    Pagada online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-amber-50 text-amber-700">
                    <i className="ri-time-line" />
                    Pendiente de pago
                  </span>
                )}
              </>
            )}

            {!hasPrice && !isPast && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-brand-gray-100 text-brand-gray-500">
                Gratuita
              </span>
            )}
          </div>

          <div>
            <h3 className="font-serif text-brand-black text-lg font-medium">
              {appt.service_type}
            </h3>
            {type?.description && (
              <p className="font-sans text-brand-gray-500 text-sm mt-0.5">
                {type.description} · {type.duration_minutes} min
              </p>
            )}
            {!type?.description && type?.duration_minutes && (
              <p className="font-sans text-brand-gray-500 text-sm mt-0.5">
                Duración: {type.duration_minutes} min
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-brand-gray-500 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-brand-gray-400" />
              {appt.name}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-mail-line text-brand-gray-400" />
              {appt.email}
            </span>
            {appt.phone && (
              <span className="flex items-center gap-1.5">
                <i className="ri-phone-line text-brand-gray-400" />
                {appt.phone}
              </span>
            )}
          </div>

          {hasPrice && (
            <div className="flex items-center gap-3 pt-1">
              <span className="font-sans text-brand-black text-lg font-medium">
                €{type.price?.toFixed(2)}
              </span>
              {!appt.paid && !isPast && (
                <span className="font-sans text-brand-gray-500 text-xs">
                  El pago se realiza en el atelier al llegar, o online si la reserva lo requiere.
                </span>
              )}
            </div>
          )}

          {appt.notes && (
            <div className="bg-brand-gray-50 rounded-xl p-3 mt-2">
              <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium mb-1">
                Notas
              </p>
              <p className="font-sans text-brand-gray-600 text-sm">
                {appt.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}